import { useCallback, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { useLaundryProfile } from '@/context/LaundryProfileContext';
import { useNotifications } from '@/context/NotificationsContext';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { notificationBody, notificationMeta, notificationTitle } from '@/lib/notificationContent';
import { navItems } from './navConfig';

interface TopbarProps {
  onOpenMenu: () => void;
}

const LATEST_COUNT = 5;

export default function Topbar({ onOpenMenu }: TopbarProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { profile } = useLaundryProfile();
  const { notifications } = useNotifications();

  const laundryName = profile?.name ?? user?.laundryName ?? '';

  const [notifOpen, setNotifOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(notifRef, useCallback(() => setNotifOpen(false), []), notifOpen);
  useOnClickOutside(accountRef, useCallback(() => setAccountOpen(false), []), accountOpen);
  useOnClickOutside(langRef, useCallback(() => setLangOpen(false), []), langOpen);

  const isOrderDetail = /^\/dashboard\/orders\/.+/.test(location.pathname);
  const isDriverReviews = /^\/dashboard\/reviews\/drivers\/.+/.test(location.pathname);
  const matched = navItems.find((i) => i.to === location.pathname)
    ?? navItems.find((i) => i.to !== '/dashboard' && location.pathname.startsWith(i.to));
  const pageTitle = isOrderDetail
    ? t('nav.orders')
    : isDriverReviews
      ? t('nav.reviews')
      : matched
        ? t(matched.labelKey)
        : t('nav.home');

  const latest = notifications.slice(0, LATEST_COUNT);
  const isArabic = i18n.language.startsWith('ar');

  const changeLanguage = (lang: 'ar' | 'en') => {
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem('lb_lang', lang);
    } catch {
      // ignore storage errors
    }
    setLangOpen(false);
  };

  const openNotification = (to: string | null) => {
    setNotifOpen(false);
    navigate(to ?? '/dashboard/notifications');
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-background-200/70 bg-background-50 px-3 sm:gap-3 sm:px-4 md:px-6">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t('topbar.menu')}
        className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors duration-200 ease-smooth hover:bg-background-100 hover:text-foreground-900 lg:hidden"
      >
        <i className="ri-menu-line text-xl" aria-hidden="true" />
      </button>

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-semibold text-foreground-950 md:text-lg">{pageTitle}</h1>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        {/* Language */}
        <div className="relative" ref={langRef}>
          <button
            type="button"
            onClick={() => setLangOpen((v) => !v)}
            aria-label={t('lang.switch')}
            className="flex h-10 items-center gap-1.5 rounded-md border border-background-200 px-3 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-translate-2 text-lg" aria-hidden="true" />
            <span className="hidden sm:inline">{isArabic ? 'ع' : 'EN'}</span>
          </button>
          {langOpen ? (
            <div className="absolute end-0 top-[calc(100%+8px)] z-40 w-40 overflow-hidden rounded-lg lb-glass-strong py-1 animate-fade-in-down">
              <button
                type="button"
                onClick={() => changeLanguage('ar')}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm text-start hover:bg-background-100 cursor-pointer',
                  isArabic && 'text-primary-700',
                )}
              >
                <i className="ri-check-line w-4" aria-hidden="true" />
                {t('lang.ar')}
              </button>
              <button
                type="button"
                onClick={() => changeLanguage('en')}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm text-start hover:bg-background-100 cursor-pointer',
                  !isArabic && 'text-primary-700',
                )}
              >
                <i className="ri-check-line w-4" aria-hidden="true" />
                {t('lang.en')}
              </button>
            </div>
          ) : null}
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            aria-label={t('topbar.notifications')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-notification-3-line text-xl" aria-hidden="true" />
          </button>
          {notifOpen ? (
            <div className="absolute end-0 top-[calc(100%+8px)] z-40 w-96 max-w-[88vw] overflow-hidden rounded-lg lb-glass-strong animate-fade-in-down">
              <div className="flex items-center justify-between gap-2 border-b border-background-200 px-4 py-3">
                <p className="text-sm font-semibold text-foreground-950">{t('topbar.notifications')}</p>
                <Link
                  to="/dashboard/notifications"
                  onClick={() => setNotifOpen(false)}
                  className="text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
                >
                  {t('notifications.all')}
                </Link>
              </div>

              {latest.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-foreground-500">
                  {t('notifications.empty')}
                </p>
              ) : (
                <ul className="max-h-80 overflow-y-auto">
                  {latest.map((n) => {
                    const meta = notificationMeta(n.kind);
                    return (
                      <li key={n.id} className="border-b border-background-100 last:border-0">
                        <button
                          type="button"
                          onClick={() => openNotification(n.to)}
                          className="flex w-full gap-3 px-4 py-3 text-start transition-colors hover:bg-background-100 cursor-pointer"
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                              meta.tone,
                            )}
                          >
                            <i className={cn(meta.icon, 'text-base leading-none')} aria-hidden="true" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-foreground-900">
                              {notificationTitle(n, t)}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-foreground-500">
                              {notificationBody(n, t)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="border-t border-background-200 p-2">
                <Link
                  to="/dashboard/notifications"
                  onClick={() => setNotifOpen(false)}
                  className="flex h-9 items-center justify-center gap-1.5 rounded-md text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  {t('notifications.all')}
                  <i className="ri-arrow-left-line text-base rtl:rotate-180" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ) : null}
        </div>

        {/* Account */}
        <div className="relative" ref={accountRef}>
          <button
            type="button"
            onClick={() => setAccountOpen((v) => !v)}
            className="flex h-10 items-center gap-2 rounded-md border border-background-200 px-2.5 hover:bg-background-100 cursor-pointer"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-xs font-bold text-background-50">
              {profile?.logoUrl ? (
                <img src={profile.logoUrl} alt={laundryName} className="h-full w-full object-cover" />
              ) : (
                laundryName.trim().charAt(0) || 'L'
              )}
            </span>
            <span className="hidden max-w-[140px] truncate text-sm font-medium text-foreground-800 sm:inline">
              {laundryName}
            </span>
            <i className="ri-arrow-down-s-line hidden text-lg text-foreground-400 sm:block" aria-hidden="true" />
          </button>
          {accountOpen ? (
            <div className="absolute end-0 top-[calc(100%+8px)] z-40 w-64 overflow-hidden rounded-lg lb-glass-strong animate-fade-in-down">
              <div className="border-b border-background-200 px-4 py-3">
                <p className="truncate text-sm font-semibold text-foreground-950">{laundryName}</p>
                <p className="lb-ltr-field truncate text-xs text-foreground-500">{user?.email}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/dashboard/settings"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className="ri-settings-3-line text-lg" aria-hidden="true" />
                  {t('nav.settings')}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-start text-sm text-rose-600 hover:bg-rose-50 cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-lg" aria-hidden="true" />
                  {t('topbar.logout')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}